import React, { useState } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { usersAPI, apiUtils } from "../services/api";
import { APP_NAME, APP_DESCRIPTION } from "../constants/app";
import logo from "../assets/tienda-logo-alpha.png";
import loginBg from "../assets/login-bg.jpg";

const LoginContainer = styled(Container)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "100vh",
  minWidth: "100vw",
  width: "100%",
  padding: 0,
  margin: 0,
  backgroundColor: theme.palette.background.default,
  backgroundImage: `url(${loginBg})`,
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  backgroundAttachment: "fixed",
  backgroundPosition: "center center",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    zIndex: 0,
  },
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  width: "100%",
  maxWidth: 400,
  textAlign: "center",
  position: "relative",
  zIndex: 1,
  backgroundColor: "rgba(47, 46, 46, 0.85)",
}));

const LoginForm = styled("form")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({
    username: "",
    password: "",
    tenant_code: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await usersAPI.login(credentials);

      const formatted = apiUtils.formatResponse(response);

      const userData =
        Array.isArray(formatted) && formatted.length > 0
          ? formatted[0]
          : formatted;

      if (userData) {
        localStorage.setItem("user", JSON.stringify(userData));
        onLogin(userData);
      } else {
        setError("Login failed. Invalid credentials.");
      }
    } catch (err) {
      console.error("Login error:", err);
      const friendly = apiUtils.handleError(err);
      setError(friendly);

      // Fallback to demo login if API fails
      if (
        credentials.username === "admin" &&
        credentials.password === "admin"
      ) {
        const user = {
          id: 1,
          username: "admin",
          role: "admin",
        };
        localStorage.setItem("user", JSON.stringify(user));
        onLogin(user);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginPaper elevation={3}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
          }}
        >
          <img
            src={logo}
            alt={`${APP_NAME} Logo`}
            style={{ height: "64px", width: "auto", marginBottom: "16px" }}
          />
          <Typography variant="h4" component="h1" gutterBottom>
            {APP_NAME}
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {APP_DESCRIPTION}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <LoginForm onSubmit={handleSubmit}>
          <TextField
            name="tenant_code"
            label="Tenant Code"
            value={credentials.tenant_code}
            onChange={handleChange}
            required
            fullWidth
            autoFocus
          />
          <TextField
            name="username"
            label="Username"
            value={credentials.username}
            onChange={handleChange}
            required
            fullWidth
          />
          <TextField
            name="password"
            label="Password"
            type="password"
            value={credentials.password}
            onChange={handleChange}
            required
            fullWidth
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ mt: 2 }}
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </LoginForm>
      </LoginPaper>
    </LoginContainer>
  );
};

export default Login;
